import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    const checkAuthStatus = useCallback(async (token) => {
        if (!token) {
            console.log('Token bulunamadı');
            return false;
        }

        try {
            console.log('Token doğrulanıyor:', token.substring(0, 20) + '...');
            const response = await fetch('https://saticiyz-backend.saticiyiz.workers.dev/users/verify', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ token })
            });

            const data = await response.json();
            console.log('Doğrulama yanıtı:', data);

            if (!response.ok) {
                throw new Error(data.message || 'Token doğrulama başarısız');
            }

            return data.valid && data.userData ? data.userData : false;
        } catch (error) {
            console.error('Token doğrulama hatası:', error.message);
            return false;
        }
    }, []);

    const initAuth = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            console.log('Mevcut token:', token ? 'Var' : 'Yok');

            if (!token) {
                console.log('Token bulunamadı, çıkış yapılıyor');
                setLoading(false);
                return;
            }

            // API'den güncel kullanıcı bilgilerini al
            const userData = await checkAuthStatus(token);
            console.log('Token doğrulama sonucu:', userData ? 'Başarılı' : 'Başarısız');

            if (!userData) {
                throw new Error('Token doğrulanamadı');
            }
            
            setIsAuthenticated(true);
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
            
            // Kullanıcının abonelik durumunu kontrol et
            const redirectBasedOnSubscription = () => {
                console.log('Abonelik durumu kontrol ediliyor:', userData.subscription_status);
                
                // Eğer kullanıcı pricing sayfasında ve aktif aboneliği veya aktif deneme süresi varsa dashboard'a yönlendir
                const needsSubscription = checkIfNeedsSubscription(userData);
                const isPricingPage = location.pathname.includes('/pricing');
                
                // Deneme süresi veya aktif aboneliği varsa ve pricing sayfasındaysa dashboard'a yönlendir
                if (!needsSubscription && isPricingPage) {
                    console.log('Aktif abonelik veya deneme süresi var, dashboard\'a yönlendiriliyor');
                    const defaultPath = userData.role === 'admin' ? '/admin' : '/dashboard';
                    navigate(defaultPath, { replace: true });
                    return;
                }
                
                // Deneme süresi veya aboneliği yoksa ve pricing sayfasında değilse pricing'e yönlendir
                if (needsSubscription && !isPricingPage) {
                    console.log('Abonelik gerekli, pricing sayfasına yönlendiriliyor');
                    navigate('/pricing', { replace: true });
                    
                    if (userData.subscription_status === 'trial' && userData.trial_end_date) {
                        import('react-toastify').then(({ toast }) => {
                            toast.error('Deneme süreniz sona erdi. Hizmetlerimize erişmek için lütfen bir abonelik planı seçin.');
                        });
                    }
                    return;
                } 
                
                // Login veya ana sayfada ise dashboard'a yönlendir
                if (location.pathname === '/login' || location.pathname === '/') {
                    const defaultPath = userData.role === 'admin' ? '/admin' : '/dashboard';
                    console.log('Ana sayfa veya login\'den dashboard\'a yönlendiriliyor');
                    navigate(defaultPath, { replace: true });
                    return;
                }
            };
            
            redirectBasedOnSubscription();
        } catch (error) {
            console.error('Kimlik doğrulama hatası:', error.message);
            setIsAuthenticated(false);
            setUser(null);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
        } finally {
            setLoading(false);
        }
    }, [checkAuthStatus, navigate, location.pathname]);

    // Kullanıcının aboneliğe ihtiyacı olup olmadığını kontrol eden fonksiyon
    const checkIfNeedsSubscription = (userData) => {
        // Aktif aboneliği varsa ödeme gerekmez
        if (userData.subscription_status === 'active') {
            return false;
        }
        
        // Deneme süreci aktifse ve süresi dolmamışsa ödeme gerekmez
        if (userData.subscription_status === 'trial' && userData.trial_end_date) {
            const trialEndDate = new Date(userData.trial_end_date);
            const now = new Date();
            
            if (now < trialEndDate) {
                console.log('Deneme süresi aktif:', trialEndDate);
                return false;
            } else {
                console.log('Deneme süresi sona ermiş:', trialEndDate);
                return true;
            }
        }
        
        // Diğer tüm durumlarda abonelik gerekir
        return true;
    };

    useEffect(() => {
        console.log('AuthContext başlatılıyor');
        initAuth();
    }, [initAuth]);

    const login = useCallback((token, userData) => {
        console.log('Login işlemi başlatıldı');
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        setIsAuthenticated(true);
        setUser(userData);
        console.log('Login işlemi tamamlandı');
    }, []);

    const logout = useCallback(() => {
        console.log('Logout işlemi başlatıldı');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
        setUser(null);
        navigate('/login', { replace: true });
        console.log('Logout işlemi tamamlandı');
    }, [navigate]);

    if (loading) {
        return (
            <div className="fixed inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    const contextValue = {
        isAuthenticated,
        user,
        login,
        logout,
        checkAuthStatus
    };

    console.log('AuthContext durumu:', { isAuthenticated, hasUser: !!user });

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};
